import { analyticsRepository } from "@/repositories/analytics";
import { useStore } from "@/stores/useStore";
import { useCallback, useRef } from "react";
import {
  InboxPanelToggleEvent,
  InboxPanelTabNavigationEvent,
  InboxPanelTriggerSource,
  InboxPanelTab,
  InboxPanelNavigationMethod,
  ANALYTICS_EVENTS,
} from "@/types/v2/Analytics";
import { LOCAL_STORAGE_BASE_KEYS } from "@/types/v2/LocalStorage";

/**
 * Hook for tracking Inbox Panel analytics events.
 *
 * Provides methods to track user interactions with the Inbox Panel
 * including panel toggle actions, tab navigation, and session timing.
 * All tracking follows existing analytics patterns and includes proper error handling.
 *
 * @example
 * ```typescript
 * const { trackPanelToggle, trackTabNavigation, startSession, endSession } = useInboxPanelAnalytics();
 *
 * // Track when user opens the panel
 * trackPanelToggle("opened", "header_button", 400, false);
 *
 * // Track when user switches tabs
 * trackTabNavigation("activityFeed", "messageBoard", 15000, "click");
 * ```
 */
export const useInboxPanelAnalytics = () => {
  const shopId = useStore((s) => s.shopId);
  const userId = useStore((s) => s.user?.uid);

  // Session tracking refs
  const sessionStartTime = useRef<number | null>(null);
  const currentTabStartTime = useRef<number | null>(null);
  const currentTab = useRef<InboxPanelTab | null>(null);

  /**
   * Wrapper function for analytics events that adds common parameters
   * and handles missing shop/user context gracefully.
   */
  const analyticsWrapper = useCallback(
    (
      eventName: string,
      eventParams: Omit<
        InboxPanelToggleEvent | InboxPanelTabNavigationEvent,
        "shopId" | "userId" | "timestamp"
      >
    ) => {
      if (!shopId) {
        console.error(
          "Shop ID is missing, cannot log analytics event:",
          eventName
        );
        return;
      }

      if (!userId) {
        console.error(
          "User ID is missing, cannot log analytics event:",
          eventName
        );
        return;
      }

      try {
        analyticsRepository.logAnalyticsEvent(eventName, {
          ...eventParams,
          shopId,
          userId,
          timestamp: Date.now(),
        });
      } catch (error) {
        console.error(
          "Failed to log Inbox Panel analytics event:",
          eventName,
          error
        );
      }
    },
    [shopId, userId]
  );

  /**
   * Checks if this is the user's first time opening the inbox panel
   * by looking for any previous panel state in localStorage.
   */
  const isFirstTimeOpen = useCallback((): boolean => {
    try {
      const hasOpenedBefore = localStorage.getItem(
        LOCAL_STORAGE_BASE_KEYS.INBOX_PANEL_OPEN
      );
      return hasOpenedBefore === null;
    } catch (error) {
      console.warn("Could not check first-time open status:", error);
      return false;
    }
  }, []);

  /**
   * Tracks when a user opens or closes the Inbox Panel.
   *
   * Captures panel usage patterns and engagement metrics including
   * trigger source, panel configuration, and session duration to
   * understand user workflow preferences and optimize panel behavior.
   *
   * @param action - Whether the panel was "opened" or "closed"
   * @param triggerSource - What triggered the panel action
   * @param panelWidth - Current width of the panel in pixels
   * @param isFirstTime - Whether this is the user's first time opening (optional, will auto-detect)
   * @param sessionDurationMs - Duration panel was open (for close actions, optional)
   *
   * @example
   * ```typescript
   * // Track panel opening from header button
   * trackPanelToggle("opened", "header_button", 400);
   *
   * // Track panel closing with session duration
   * trackPanelToggle("closed", "header_button", 400, false, 120000);
   *
   * // Track panel opening from keyboard shortcut
   * trackPanelToggle("opened", "keyboard_shortcut", 350, true);
   * ```
   */
  const trackPanelToggle = useCallback(
    (
      action: "opened" | "closed",
      triggerSource: InboxPanelTriggerSource,
      panelWidth: number,
      isFirstTime?: boolean,
      sessionDurationMs?: number
    ) => {
      const eventParams: Omit<
        InboxPanelToggleEvent,
        "shopId" | "userId" | "timestamp"
      > = {
        action,
        triggerSource,
        panelWidth,
        isFirstTimeOpen: isFirstTime ?? isFirstTimeOpen(),
      };

      // Handle session tracking
      if (action === "opened") {
        sessionStartTime.current = Date.now();
        currentTabStartTime.current = Date.now();

        // Mark that panel has been opened in localStorage
        try {
          localStorage.setItem(
            LOCAL_STORAGE_BASE_KEYS.INBOX_PANEL_OPEN,
            "true"
          );
        } catch (error) {
          console.warn("Could not update panel open status:", error);
        }
      } else if (action === "closed") {
        // Calculate session duration if not provided
        if (sessionDurationMs === undefined && sessionStartTime.current) {
          eventParams.sessionDurationMs = Date.now() - sessionStartTime.current;
        } else if (sessionDurationMs !== undefined) {
          eventParams.sessionDurationMs = Math.round(sessionDurationMs);
        }

        // Reset session tracking
        sessionStartTime.current = null;
        currentTabStartTime.current = null;
        currentTab.current = null;
      }

      analyticsWrapper(ANALYTICS_EVENTS.INBOX_PANEL_TOGGLE, eventParams);
    },
    [analyticsWrapper, isFirstTimeOpen]
  );

  /**
   * Tracks when a user navigates between tabs within the Inbox Panel.
   *
   * Captures tab navigation patterns and time spent in each section
   * to understand user preferences and optimize tab organization.
   * Automatically calculates time spent in previous tab based on session tracking.
   *
   * @param fromTab - Tab the user was previously viewing
   * @param toTab - Tab the user navigated to
   * @param timeInPreviousTabMs - Time spent in previous tab (optional, will auto-calculate)
   * @param navigationMethod - Method used to navigate between tabs
   *
   * @example
   * ```typescript
   * // Track tab navigation from Activity Feed to Message Board
   * trackTabNavigation("activityFeed", "messageBoard", undefined, "click");
   *
   * // Track tab navigation with specific timing
   * trackTabNavigation("messageBoard", "goList", 30000, "keyboard");
   *
   * // Track touch navigation on mobile
   * trackTabNavigation("goList", "activityFeed", 5000, "touch");
   * ```
   */
  const trackTabNavigation = useCallback(
    (
      fromTab: InboxPanelTab,
      toTab: InboxPanelTab,
      timeInPreviousTabMs?: number,
      navigationMethod: InboxPanelNavigationMethod = "click"
    ) => {
      // Calculate time in previous tab if not provided
      let calculatedTime = timeInPreviousTabMs;
      if (calculatedTime === undefined && currentTabStartTime.current) {
        calculatedTime = Date.now() - currentTabStartTime.current;
      }

      const eventParams: Omit<
        InboxPanelTabNavigationEvent,
        "shopId" | "userId" | "timestamp"
      > = {
        fromTab,
        toTab,
        timeInPreviousTabMs: calculatedTime ? Math.round(calculatedTime) : 0,
        navigationMethod,
      };

      analyticsWrapper(
        ANALYTICS_EVENTS.INBOX_PANEL_TAB_NAVIGATION,
        eventParams
      );

      // Update current tab tracking
      currentTab.current = toTab;
      currentTabStartTime.current = Date.now();
    },
    [analyticsWrapper]
  );

  /**
   * Starts a new session for panel tracking.
   * Useful for manual session management or when panel state is restored.
   *
   * @param initialTab - The tab that is initially active
   *
   * @example
   * ```typescript
   * // Start tracking when panel opens with Activity Feed active
   * startSession("activityFeed");
   * ```
   */
  const startSession = useCallback((initialTab: InboxPanelTab) => {
    sessionStartTime.current = Date.now();
    currentTabStartTime.current = Date.now();
    currentTab.current = initialTab;
  }, []);

  /**
   * Ends the current session and returns the session duration.
   * Useful for manual session management.
   *
   * @returns Session duration in milliseconds, or null if no session was active
   *
   * @example
   * ```typescript
   * // End session and get duration
   * const duration = endSession();
   * if (duration) {
   *   console.log(`Session lasted ${duration}ms`);
   * }
   * ```
   */
  const endSession = useCallback((): number | null => {
    if (!sessionStartTime.current) {
      return null;
    }

    const duration = Date.now() - sessionStartTime.current;
    sessionStartTime.current = null;
    currentTabStartTime.current = null;
    currentTab.current = null;

    return duration;
  }, []);

  /**
   * Gets the current session status and timing information.
   * Useful for debugging or conditional analytics.
   *
   * @returns Object with session state and timing information
   */
  const getSessionInfo = useCallback(() => {
    const now = Date.now();
    return {
      isSessionActive: sessionStartTime.current !== null,
      sessionDuration: sessionStartTime.current
        ? now - sessionStartTime.current
        : null,
      currentTab: currentTab.current,
      timeInCurrentTab: currentTabStartTime.current
        ? now - currentTabStartTime.current
        : null,
    };
  }, []);

  return {
    trackPanelToggle,
    trackTabNavigation,
    startSession,
    endSession,
    getSessionInfo,
  };
};
